<script setup>
import MultiSelect from 'primevue/multiselect';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: [Array, String, Number], // Allow multiple types, but Array is preferred
        default: () => []
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    options: {
        type: Array,
        default: () => [],
        validator: (value) => {
            return value.every((item) => 'value' in item && ('text' in item || 'disptext' in item)); //check disptext
        }
    },
    visible: {
        type: Boolean,
        default: true
    },
    placeholder: {
        type: String,
        default: '선택한 항목이 없습니다.'
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    emptyMessage: {
        type: String,
        default: 'No results found.'
    },
    height: {
        type: String,
        default: '3rem'
    },
    customLabel: {
        /* default 일 경우 선택한 항목이 표시됨 , true 일 경우 첫번째 선택 외 갯수로 표시 */
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'input', 'change']);
const selectedItems = ref([]);
const items = ref([]);
const ValidationMsg = ref('');
const selected = computed(() => (selectedItems.value.length === 0 ? '' : selectedItems.value.length === 1 ? selectedItems.value[0]?.text : selectedItems.value[0]?.text + `외 ${selectedItems.value.length - 1}건 선택`));

const updateItems = (options) => {
    items.value = options.map((option) => ({
        value: option.value.toString(),
        text: option.disptext || option.text
    }));
};

const initializeSelectedItems = () => {
    selectedItems.value = [];

    if (Array.isArray(props.modelValue)) {
        // modelValue가 배열일 경우
        props.modelValue.forEach((value) => {
            let targetValue;
            if (typeof value === 'object' && value !== null && 'value' in value) {
                // 객체 형태일 경우
                targetValue = value.value.toString();
            } else {
                // 단순 값일 경우
                targetValue = value.toString();
            }

            // items에서 해당 값을 찾음
            const item = items.value.find((option) => option.value === targetValue);
            if (item) {
                selectedItems.value.push(item);
            }
        });
    } else if (props.modelValue != null) {
        // modelValue가 단일 값일 경우
        let stringValue;
        if (typeof props.modelValue === 'object' && props.modelValue !== null && 'value' in props.modelValue) {
            // 객체 형태일 경우
            stringValue = props.modelValue.value.toString();
        } else {
            // 단순 값일 경우
            stringValue = props.modelValue.toString();
        }

        // items에서 해당 값을 찾음
        const item = items.value.find((option) => option.value === stringValue);
        if (item) {
            selectedItems.value.push(item);
        }
    }
};

const emitUpdate = (value) => {
    const emitValue = value.map((item) => item.value);
    emit('update:modelValue', emitValue);
    emit('input', { id: props.inputId, value: emitValue });
};

watch(
    () => props.options,
    (newOptions) => {
        updateItems(newOptions);
        nextTick(() => initializeSelectedItems());
    },
    { immediate: true }
);

watch(() => props.modelValue, initializeSelectedItems, { deep: true });

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
    }
);

const onChange = (event) => {
    ValidationMsg.value = '';
    selectedItems.value = event.value.map((value) => {
        return items.value.find((item) => item.value === value) || { value, text: value };
    });
    emitUpdate(selectedItems.value);
};

const clearInput = () => {
    selectedItems.value = [];
    emitUpdate(selectedItems.value);
};

const selectedOptions = computed({
    get() {
        return selectedItems.value.map((item) => item.value);
    },
    set(newVal) {
        selectedItems.value = items.value.filter((item) => newVal.includes(item.value));
        emitUpdate(selectedItems.value);
    }
});

onMounted(() => {
    updateItems(props.options);
    initializeSelectedItems();
});
</script>

<template>
    <div v-if="visible" class="flex flex-col">
        <label :class="['label-font mb-1 mx-1', required ? 'label-required' : 'text-gray-700 dark:text-gray-300']">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>
        <div :class="['custom-multiselect', { 'custom-label-enabled': props.customLabel }]">
            <MultiSelect
                :id="inputId"
                v-model="selectedOptions"
                :options="items"
                optionLabel="text"
                optionValue="value"
                :disabled="disabled || readonly"
                @change="onChange"
                :class="['custom-multiselect-input', { 'error-border': ValidationMsg }, { 'readonly-bg': readonly }]"
                :placeholder="placeholder"
                display="chip"
                :filter="true"
                :style="{ height }"
            >
                <template #empty>
                    {{ emptyMessage }}
                </template>
                <template #footer>
                    <div class="dropdown-footer">Total {{ items.length }}</div>
                </template>
                <template #option="slotProps">
                    <div class="custom-option">{{ slotProps.option.text }}</div>
                </template>
            </MultiSelect>
            <span v-if="customLabel && selected !== ''" class="selected-count"> {{ selected }} </span>
        </div>
    </div>
</template>

<style scoped>
.label-font {
    font-family: 'SCoreDream600';
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171;
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.custom-multiselect {
    position: relative;
}

.error-border {
    border-color: #f87171 !important;
}

.readonly-bg {
    background-color: whitesmoke !important;
}

.custom-multiselect-input {
    width: 100%;
    font-size: 0.875rem;
    font-family: 'SCoreDream400';
    border-radius: 0.5rem;
    border: 1px solid #d1d5db;
    height: 3rem;
    background-color: white;
    cursor: pointer;
}

.custom-option {
    font-size: 0.875rem;
    font-family: 'SCoreDream400';
    padding: 0.1rem;
}

.p-multiselect-item {
    font-size: 0.9rem;
}

/* Center content horizontally in the input box */
/* .custom-multiselect :deep(.p-multiselect-label) {
    display: flex;
    align-items: center;
    justify-content: start;
    height: 100%;
    padding: 0 2.5rem 0 0.5rem;
} */

/* Center placeholder horizontally */
.custom-multiselect :deep(.p-multiselect-label.p-placeholder) {
    display: flex;
    align-items: center;
    justify-content: start;
}

.custom-multiselect :deep(.p-checkbox > .p-checkbox-box.p-highlight) {
    border-color: #0038ea !important;
    background: #0038ea !important;
}

.custom-multiselect :deep(.p-checkbox > .p-checkbox-box.p-highlight:hover) {
    border-color: #0038ea !important;
    background: #0038ea !important;
}

.custom-multiselect :deep(.p-checkbox > .p-checkbox-box.p-focus) {
    box-shadow: 0 0 0 0.1rem rgba(0, 56, 234, 0.2) !important;
    border-color: #0038ea !important;
}

.custom-multiselect :deep(.p-multiselect) {
    font-size: 0.8rem;
    font-family: 'SCoreDream400';
}

.custom-multiselect.custom-label-enabled :deep(.p-multiselect-chip) {
    display: none !important;
}

.custom-multiselect.custom-label-enabled :deep(.p-chip-remove-icon) {
    display: none !important;
}

/* customLabel이 false일 때 (기본 스타일) */
.custom-multiselect :deep(.p-multiselect-chip-item) {
    background-color: transparent !important;
}

.custom-multiselect :deep(.p-multiselect-chip) {
    background-color: transparent !important;
    border: 0.1rem solid #d1d5db !important;
    cursor: default;
}

.custom-multiselect :deep(.p-chip-remove-icon) {
    color: #d1d5db !important;
    width: 0.85rem !important;
    cursor: pointer;
}

.custom-multiselect :deep(.p-chip-remove-icon :hover) {
    /* color: red; */
}

.selected-count {
    font-size: 0.875rem;
    font-family: 'SCoreDream400';
    color: gray;
    padding: 0.2rem;
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.dropdown-footer {
    font-size: 0.8rem;
    color: gray;
    padding: 0.2rem;
    text-align: right;
    margin-right: 0.5rem;
}

.clear-button {
    position: absolute;
    right: 2.5rem;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    width: 1.6rem;
    height: 1.6rem;
    font-family: 'SCoreDream400';
    font-size: 1rem;
    background: transparent;
    border: none;
    opacity: 0.5;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
}

.clear-button:hover {
    font-family: 'SCoreDream500';
    opacity: 1;
}
</style>
